import {
  TypographyCaption,
  TypographyP2,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, ChevronRight, Loader2, ShieldCheck } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  formatEligibilityAmount,
  patientDisplayName,
  type EligibilityPatientRow,
  type EligibilityPayload,
} from '../../../contracts/insuranceArtifacts';

const DEFAULT_MAX_VISIBLE = 5;

export interface EligibilityCoverageBlockProps {
  status: 'fetching' | 'completed' | 'error';
  payload?: EligibilityPayload;
  error?: string;
  className?: string;
  /** Cap rows in the inline card; omit (or Infinity) in the canvas panel. */
  maxVisible?: number;
  onViewAll?: (payload: EligibilityPayload) => void;
}

const statusLine = (row: EligibilityPatientRow): string => {
  const parts = [
    row.planStatus ?? row.status,
    row.payer,
    formatEligibilityAmount(row.copay) !== undefined
      ? `Copay ${formatEligibilityAmount(row.copay)}`
      : undefined,
  ].filter(Boolean);
  return parts.join(' · ');
};

function PatientRow({
  row,
  onOpenArtifact,
}: {
  row: EligibilityPatientRow;
  onOpenArtifact?: () => void;
}) {
  const content = (
    <>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TypographyPBold className="truncate text-text-primary">
          {patientDisplayName(row)}
        </TypographyPBold>
        <TypographyCaption className="truncate text-text-secondary">
          {statusLine(row) || row.memberId || row.id}
        </TypographyCaption>
      </div>
      <ChevronRight className="size-4 shrink-0 text-text-secondary opacity-0 group-hover:opacity-100" />
    </>
  );

  const className =
    'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-hover';

  if (onOpenArtifact) {
    return (
      <button type="button" onClick={onOpenArtifact} className={className}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}

export function EligibilityCoverageBlock({
  status,
  payload,
  error,
  className,
  maxVisible = DEFAULT_MAX_VISIBLE,
  onViewAll,
}: EligibilityCoverageBlockProps) {
  const intl = useIntl();

  if (status === 'fetching') {
    return (
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl border border-border bg-surface-page px-4 py-3',
          className
        )}
        data-testid="eligibility-coverage-block-loading"
      >
        <Loader2 className="size-4 animate-spin text-text-secondary" />
        <TypographyP2 className="text-text-secondary">
          <FormattedMessage defaultMessage="Checking eligibility…" id="PRsyvsFHKX" />
        </TypographyP2>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex items-start gap-3 rounded-xl border border-border bg-surface-page px-4 py-3',
          className
        )}
        data-testid="eligibility-coverage-block-error"
      >
        <AlertCircle className="mt-0.5 size-4 shrink-0 text-text-secondary" />
        <TypographyP2 className="text-text-secondary">
          {error ?? (
            <FormattedMessage
              defaultMessage="Could not load eligibility."
              id="+c1I8RK8LN"
            />
          )}
        </TypographyP2>
      </div>
    );
  }

  const patients = payload?.patients ?? [];
  const visible = patients.slice(0, maxVisible);
  const remaining = Math.max(0, patients.length - visible.length);

  const canOpenArtifact =
    Boolean(payload && onViewAll) && (patients.length > 0 || Boolean(payload?.warning));
  const openArtifact =
    canOpenArtifact && payload && onViewAll ? () => onViewAll(payload) : undefined;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-surface-page',
        className
      )}
      data-testid="eligibility-coverage-block"
    >
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-3 border-b border-border px-4 py-3 text-start transition-colors',
          openArtifact && 'hover:bg-background-secondary-hover'
        )}
        onClick={openArtifact}
        disabled={!openArtifact}
        data-testid="eligibility-coverage-view-all"
      >
        <div className="flex size-10 flex-col items-center justify-center rounded-lg bg-entity-blue-muted text-entity-blue-foreground">
          <ShieldCheck className="size-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <TypographyPBold className="truncate">
            <FormattedMessage defaultMessage="Eligibility" id="BFaN95+zSM" />
          </TypographyPBold>
          <TypographyCaption className="truncate text-text-secondary">
            {[
              payload?.query ? `“${payload.query}”` : null,
              payload?.provider,
              intl.formatMessage(
                {
                  defaultMessage: '{count, plural, one {# patient} other {# patients}}',
                  id: 'ca0LK3rYh2',
                },
                { count: patients.length }
              ),
            ]
              .filter(Boolean)
              .join(' · ')}
          </TypographyCaption>
        </div>
        {openArtifact ? (
          <span className="shrink-0 text-sm font-medium text-text-primary underline-offset-2">
            <FormattedMessage defaultMessage="Open" id="JfG49wNHKP" />
          </span>
        ) : null}
      </button>

      {payload?.warning ? (
        <div className="border-b border-border px-4 py-2">
          <TypographyCaption className="text-text-secondary">
            {payload.warning}
          </TypographyCaption>
        </div>
      ) : null}

      {patients.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage
              defaultMessage="No coverage results in this batch."
              id="Ugq+HR/qI1"
            />
          </TypographyP2>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {visible.map((row) => (
            <li key={row.id}>
              <PatientRow row={row} onOpenArtifact={openArtifact} />
            </li>
          ))}
        </ul>
      )}

      {remaining > 0 && openArtifact ? (
        <button
          type="button"
          className="w-full border-t border-border px-4 py-2 text-start transition-colors hover:bg-surface-hover"
          onClick={openArtifact}
        >
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage
              defaultMessage="+{count} more — open eligibility"
              id="ZrlCc8k0jg"
              values={{ count: remaining }}
            />
          </TypographyCaption>
        </button>
      ) : remaining > 0 ? (
        <div className="border-t border-border px-4 py-2">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage
              defaultMessage="+{count} more"
              id="/zFGgPmQSV"
              values={{ count: remaining }}
            />
          </TypographyCaption>
        </div>
      ) : null}
    </div>
  );
}
